import { useApiMutation, useApiQuery } from "@/hooks/use-api";
import { getUserToken } from "@/utils/access-provider";
import { Content } from "@/models/document";
import { Enrichment, EnrichmentGalleryItem } from "@/models/enrichment";
import { AssistantMode } from "@/models/conversation";
import { useQuery } from "@tanstack/react-query";
import { API_BASE_URL } from "@/api/core";

// Mock data pour les enrichissements disponibles
const ENRICHMENT_GALLERY_ITEMS: EnrichmentGalleryItem[] = [
  {
    id: "sentiment",
    title: "Analyse de sentiment",
    description: "Analysez le sentiment et l'émotion dans vos textes",
    type: "sentiment_analysis",
    icon: "fa-heart",
    color: "var(--arc-error-main, #EF4444)",
    examples: [
      "Analyse des avis clients",
      "Monitoring des réseaux sociaux",
      "Évaluation de la satisfaction",
    ],
    features: [
      "Sentiment positif/négatif/neutre",
      "Détection d'émotions",
      "Analyse par segments",
      "Tendances temporelles",
    ],
    isAvailable: true,
    isPremium: false,
  },
  {
    id: "stt",
    title: "Speech-to-Text",
    description:
      "Convertissez vos fichiers audio en texte avec une précision élevée",
    type: "speech_to_text",
    icon: "fa-microphone",
    color: "var(--arc-success-main, #10B981)",
    examples: [
      "Transcription de réunions",
      "Sous-titrage automatique",
      "Notes vocales converties",
    ],
    features: [
      "Support multi-langues",
      "Reconnaissance de locuteurs",
      "Ponctuation automatique",
      "Filtrage du bruit",
    ],
    isAvailable: true,
    isPremium: true,
  },
  {
    id: "concepts",
    title: "Extraction de Concepts",
    description:
      "Extrayez automatiquement les concepts clés et liez-les à des pages Wikipedia",
    type: "concept_extraction",
    icon: "fa-lightbulb",
    color: "var(--arc-warning-main, #F59E0B)",
    examples: [
      "Concepts clés d'articles",
      "Liens vers Wikipedia",
      "Cartographie thématique",
    ],
    features: [
      "Extraction intelligente",
      "Liens Wikipedia",
      "Regroupement thématique",
    ],
    isAvailable: true,
    isPremium: false,
  },
  {
    id: "logo",
    title: "Détection de Logos",
    description:
      "Détectez et identifiez automatiquement les logos de marques dans vos images",
    type: "logo_detection",
    icon: "fa-image",
    color: "var(--arc-chart-6, #9333EA)",
    examples: [
      "Détection de marques",
      "Analyse d'images publicitaires",
      "Monitoring visuel",
    ],
    features: [
      "Détection multi-logos",
      "Base de marques étendue",
      "Analyse d'images",
    ],
    isAvailable: true,
    isPremium: false,
  },
  {
    id: "emotions",
    title: "Détection d'émotions",
    description:
      "Détectez les émotions exprimées dans vos textes (joie, colère, peur, surprise, etc.)",
    type: "emotion_detection",
    icon: "fa-smile",
    color: "var(--arc-chart-8, #F472B6)",
    examples: [
      "Analyse d'avis clients",
      "Monitoring des réseaux sociaux",
      "Détection de crises",
    ],
    features: [
      "8 émotions de base",
      "Émotion principale et secondaires",
      "Score de confiance",
    ],
    isAvailable: true,
    isPremium: false,
  },
  {
    id: "ner",
    title: "Extraction d'entités nommées",
    description:
      "Identifiez automatiquement les personnes, organisations, lieux et autres entités dans vos textes",
    type: "named_entity_recognition",
    icon: "fa-user-tag",
    color: "var(--arc-chart-1, #6366F1)",
    examples: [
      "John Smith travaille chez Microsoft à Seattle",
      "Apple Inc. a annoncé ses résultats financiers",
      "Le 15 janvier 2024, Paris a accueilli la conférence",
    ],
    features: [
      "Détection de personnes",
      "Identification d'organisations",
      "Reconnaissance de lieux",
    ],
    isAvailable: false,
    isPremium: false,
  },
  {
    id: "classification",
    title: "Classification automatique",
    description:
      "Classez automatiquement vos documents par catégorie et sous-catégorie",
    type: "classification",
    icon: "fa-tags",
    color: "var(--arc-warning-main, #F59E0B)",
    examples: [
      "Classification d'emails",
      "Catégorisation de tickets",
      "Organisation de documents",
    ],
    features: [
      "Classification multi-niveaux",
      "Apprentissage automatique",
      "Scores de confiance",
    ],
    isAvailable: false,
    isPremium: false,
  },
  {
    id: "language",
    title: "Détection de langue",
    description: "Identifiez automatiquement la langue de vos textes",
    type: "language_detection",
    icon: "fa-globe",
    color: "var(--arc-chart-2, #8B5CF6)",
    examples: [
      "Support multilingue",
      "Routage automatique",
      "Filtrage par langue",
    ],
    features: [
      "Support de 50+ langues",
      "Confiance élevée",
      "Détection rapide",
    ],
    isAvailable: false,
    isPremium: false,
  },
  {
    id: "keywords",
    title: "Extraction de mots-clés",
    description: "Extrayez automatiquement les mots-clés les plus pertinents",
    type: "keyword_extraction",
    icon: "fa-key",
    color: "var(--arc-chart-3, #06B6D4)",
    examples: [
      "Mots-clés SEO",
      "Résumés automatiques",
      "Indexation de contenu",
    ],
    features: [
      "Extraction intelligente",
      "Pondération par importance",
      "Filtrage du bruit",
    ],
    isAvailable: false,
    isPremium: false,
  },
  {
    id: "summarization",
    title: "Résumé automatique",
    description: "Générez des résumés automatiques de vos documents",
    type: "summarization",
    icon: "fa-file-text",
    color: "var(--arc-chart-4, #84CC16)",
    examples: [
      "Résumés d'articles",
      "Synthèses de rapports",
      "Aperçus de documents",
    ],
    features: [
      "Résumés extractifs",
      "Résumés abstractifs",
      "Longueur personnalisable",
    ],
    isAvailable: false,
    isPremium: false,
  },
  {
    id: "threats",
    title: "Détection de menaces",
    description:
      "Détectez automatiquement les menaces, discours haineux, incitations à la violence et contenus dangereux",
    type: "threat_detection",
    icon: "fa-shield-alt",
    color: "var(--arc-error-dark, #DC2626)",
    examples: [
      "Détection de discours haineux",
      "Alertes de menaces en ligne",
      "Surveillance de contenus dangereux",
    ],
    features: [
      "Classification des types de menaces",
      "Score de sévérité",
      "Détection multilingue",
      "Alertes en temps réel",
    ],
    isAvailable: true,
    isPremium: false,
  },
];

// Hook pour récupérer les enrichissements disponibles
export const useEnrichmentGallery = () => {
  return useQuery({
    queryKey: ["enrichment-gallery"],
    queryFn: async (): Promise<EnrichmentGalleryItem[]> => {
      // Simuler un délai de chargement
      await new Promise((resolve) => setTimeout(resolve, 500));
      return ENRICHMENT_GALLERY_ITEMS;
    },
  });
};

// Create standard CRUD hooks using the factory
import { createResourceApi } from "./factory";

const enrichmentApi = createResourceApi<Enrichment, Partial<Enrichment>>({
  resourcePath: "enrichments",
  requireWorkspace: false,
});

// Hook pour récupérer les enrichissements de l'utilisateur
export const useEnrichments = enrichmentApi.useList;

// Hook pour récupérer un enrichissement spécifique
export const useEnrichment = enrichmentApi.useOne;

// Hook pour créer un nouvel enrichissement
export const useCreateEnrichment = enrichmentApi.useCreate;

// Hook pour supprimer un enrichissement
export const useDeleteEnrichment = enrichmentApi.useDelete;

// Hook pour mettre à jour un enrichissement
export const useUpdateEnrichment = enrichmentApi.useUpdate;

// Hook pour mettre à jour le groupe d'un enrichissement
export const useUpdateEnrichmentGroup = () => {
  return useApiMutation<
    Enrichment,
    { group: { id: string } | null },
    { id: string }
  >({
    url: "enrichments/{id}",
    method: "PATCH",
  });
};

// Types pour le chat LLM
export interface ChatLLMRequest {
  history: string[];
  message: string;
  mode?: AssistantMode;
}

export type ChatLLMResponse =
  | string // String response with embedded <Document> tags
  | {
      message: string;
      documents?: Content[]; // Array of Content documents
      // Ajoutez d'autres champs si nécessaire selon la réponse du backend
    };

// Hook pour envoyer un message au chat LLM
export const useChatLLM = () => {
  return useApiMutation<ChatLLMResponse, ChatLLMRequest>({
    url: "llm/chat",
    method: "POST",
  });
};

/**
 * User question from the ask_user tool
 */
export interface UserQuestionItem {
  question: string;
  options: Array<{ id: string; label: string }>;
  allowMultiple?: boolean;
}

export interface UserQuestion {
  questions: UserQuestionItem[];
  title?: string;
}

/**
 * Action confirmation request from the chat endpoint
 * Emitted when a mutating action (POST/PATCH/DELETE) is proposed in ask/plan mode
 */
export interface ActionConfirmation {
  actionId: string;
  toolName: string;
  method: string;
  path: string;
  params?: Record<string, unknown>;
  body?: Record<string, unknown>;
}

/**
 * Response from executing a confirmed action
 */
export interface ExecuteActionResponse {
  success: boolean;
  action_id: string;
  result?: unknown;
  error?: string;
}

/**
 * Stream chat messages from the LLM using Server-Sent Events
 * @param request The chat request with history and message
 * @param onChunk Callback called with each text chunk as it arrives
 * @param onDone Callback called when streaming is complete
 * @param onError Callback called if an error occurs
 * @param onToolCall Optional callback for tool call events
 * @param onToolResult Optional callback for tool result events (contains documents)
 * @param onUserQuestion Optional callback for user question events (from ask_user tool)
 * @param onActionConfirmation Optional callback for action confirmation events (mutating actions in ask/plan mode)
 * @returns AbortController to cancel the request if needed
 */
export const streamChatLLM = async (
  request: ChatLLMRequest,
  onChunk: (text: string) => void,
  onDone: () => void,
  onError: (error: Error) => void,
  onToolCall?: (toolCall: {
    name: string;
    args: Record<string, unknown>;
  }) => void,
  onToolResult?: (result: { name: string; result: unknown[] }) => void,
  onUserQuestion?: (question: UserQuestion) => void,
  onActionConfirmation?: (action: ActionConfirmation) => void,
): Promise<AbortController> => {
  const controller = new AbortController();
  const apiUrl = (API_BASE_URL || "").replace(/\/$/, "");
  const endpoint = `${apiUrl}/llm/chat`;

  // Get workspace ID from localStorage
  let workspaceId: string | null = null;
  try {
    const stored = localStorage.getItem("selectedWorkspaceId");
    if (stored) {
      workspaceId = JSON.parse(stored);
    }
  } catch {
    // Ignore JSON parse errors
  }

  // Get API key using the unified access provider helper
  const apiKey = getUserToken() || "";

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
        ...(workspaceId && { "X-Workspace-Id": workspaceId }),
      },
      body: JSON.stringify(request),
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("No response body");
    }

    const decoder = new TextDecoder();
    let buffer = "";
    let hasDone = false;

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        if (!hasDone) {
          hasDone = true;
          onDone();
        }
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      // Process SSE events in buffer (events are delimited by \n\n)
      const events = buffer.split("\n\n");
      buffer = events.pop() || ""; // Keep incomplete event in buffer

      for (const event of events) {
        // Each event can have multiple lines (e.g., event:, data:, id:)
        const lines = event.split("\n");
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6);

            if (data === "[DONE]") {
              if (!hasDone) {
                hasDone = true;
                onDone();
              }
              return controller;
            }

            try {
              const parsed = JSON.parse(data);
              // Handle new structured event types
              if (parsed.type === "text" && parsed.text) {
                onChunk(parsed.text);
              } else if (parsed.type === "tool_call" && onToolCall) {
                onToolCall({ name: parsed.name, args: parsed.args || {} });
              } else if (parsed.type === "tool_result" && onToolResult) {
                onToolResult({
                  name: parsed.name,
                  result: parsed.result || [],
                });
              } else if (parsed.type === "user_question" && onUserQuestion) {
                let questions: UserQuestion["questions"];
                if (parsed.questions) {
                  questions = (
                    parsed.questions as Array<Record<string, unknown>>
                  ).map((q) => ({
                    question: (q.question as string) || "",
                    options:
                      (q.options as Array<{ id: string; label: string }>) || [],
                    allowMultiple: (q.allow_multiple as boolean) || false,
                  }));
                } else {
                  questions = [
                    {
                      question: parsed.question || "",
                      options: parsed.options || [],
                      allowMultiple: parsed.allow_multiple || false,
                    },
                  ];
                }
                onUserQuestion({ questions, title: parsed.title });
              } else if (
                parsed.type === "action_confirmation" &&
                onActionConfirmation
              ) {
                // Action confirmation for mutating actions in ask/plan mode
                onActionConfirmation({
                  actionId: parsed.action_id,
                  toolName: parsed.tool_name,
                  method: parsed.method,
                  path: parsed.path,
                  params: parsed.params,
                  body: parsed.body,
                });
              }
              // Legacy format support (no type field, just text)
              else if (parsed.text && !parsed.type) {
                onChunk(parsed.text);
              }
            } catch {
              // Ignore JSON parse errors for incomplete chunks
            }
          }
        }
      }
    }
  } catch (error) {
    if ((error as Error).name !== "AbortError") {
      onError(error as Error);
    }
  }

  return controller;
};

// Hook pour exécuter un enrichissement manuellement
export const useRunEnrichment = () => {
  return useApiMutation<any, {}, { enrichment_id: string }>({
    url: "enrichments/{enrichment_id}/run_monitoring",
    method: "POST",
    params: {
      force: "True",
      run_async: "False",
    },
  });
};

/**
 * Execute a confirmed action from the chat assistant
 * Called after the user approves an action proposed in ask/plan mode
 */
export const executeConfirmedAction = async (
  action: ActionConfirmation,
): Promise<ExecuteActionResponse> => {
  const apiUrl = (API_BASE_URL || "").replace(/\/$/, "");
  const endpoint = `${apiUrl}/llm/execute-action`;

  // Get workspace ID from localStorage
  let workspaceId: string | null = null;
  try {
    const stored = localStorage.getItem("selectedWorkspaceId");
    if (stored) {
      workspaceId = JSON.parse(stored);
    }
  } catch {
    // Ignore JSON parse errors
  }

  const apiKey = getUserToken() || "";

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${apiKey}`,
      ...(workspaceId && { "X-Workspace-Id": workspaceId }),
    },
    body: JSON.stringify({
      action_id: action.actionId,
      method: action.method,
      path: action.path,
      params: action.params,
      body: action.body,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `HTTP error! status: ${response.status}, body: ${errorText}`,
    );
  }

  return response.json();
};

export interface EnrichmentStats {
  total_processed: number;
  in_progress: number;
  pending: number;
  errored: number;
  total_scope?: number;
  last_run: string | null;
  error?: string;
}

// Hook pour récupérer les statistiques d'un enrichissement
export const useEnrichmentStats = (enrichmentId?: string) => {
  return useApiQuery<EnrichmentStats>(
    { url: `enrichments/${enrichmentId}/stats` },
    { enabled: !!enrichmentId },
  );
};

// Types pour la génération de titre
export interface TitleGenerationRequest {
  message: string;
}

export interface TitleGenerationResponse {
  title: string;
}

/**
 * Generate a conversation title from the first message using LLM
 * @param message The first message of the conversation
 * @returns The generated title
 */
export const generateConversationTitle = async (
  message: string,
): Promise<string> => {
  const apiUrl = (API_BASE_URL || "").replace(/\/$/, "");
  const endpoint = `${apiUrl}/llm/title`;

  // Get workspace ID from localStorage
  let workspaceId: string | null = null;
  try {
    const stored = localStorage.getItem("selectedWorkspaceId");
    if (stored) {
      workspaceId = JSON.parse(stored);
    }
  } catch {
    // Ignore JSON parse errors
  }

  const apiKey = getUserToken() || "";

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${apiKey}`,
      ...(workspaceId && { "X-Workspace-Id": workspaceId }),
    },
    body: JSON.stringify({ message }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data: TitleGenerationResponse = await response.json();
  return data.title;
};
